import {useMemo, useState, useCallback, useEffect} from 'react';
import {createGetOneHook} from '@/utils/createGetOneHook';
import {createFieldsHook} from '@/utils/createFieldsHook';
import UsersApiService from '@/modules/users/services/UsersApiService';
import {CACHE_KEY_USERS, CACHE_KEY_LIST_USERS} from '@/modules/users/contants/users';
import {fields} from '@/modules/users/contants/users.form';
import { TermFilter } from '@dofleini/query-builder';
import {useQuery} from 'react-query';
import {useTranslation} from 'react-i18next';
import {renderStatusSelector} from '@/modules/admin/components/FieldUserStatus';
import {createSearchFieldRemoteRoles} from '@/utils/createSearchFieldRemoteRoles';
import {useSearchRol} from '@/modules/admin/hooks/useSearchRol';
import SelectUserServices from '@/modules/users/components/SelectUserServices';
import UserRegisterAccess from '@/modules/users/components/UserRegisterAccess';
import SelectUserStructures from '@/modules/users/components/SelectUserStructures';
import SelectUserEstablishments from '@/modules/users/components/SelectUserEstablishments';
import createFormFieldset from '@/utils/createFormFieldset';
import {useParams} from 'react-router-dom';
import SelectUserFacilities from '@/modules/users/components/SelectUserFacilities';
import SelectUserLabDataAccess from '@/modules/users/components/SelectUserLabDataAccess';
import {useSpace} from '@/contexts/SpaceContext';

export const useGetOneUsers = createGetOneHook(UsersApiService, 'id', CACHE_KEY_USERS);
export const useFieldsBase = createFieldsHook('users', fields);

export const useFields = (isViewMode = false, form) => {
  const fields = useFieldsBase(isViewMode);
  const {t} = useTranslation('users');
  const initialValues  = form.getFieldsValue();
  const {id} = useParams();
  const [roles, setRoles] = useState(initialValues?.roles || []);
  const [status, setStatus] = useState(initialValues?.status);
  const [clients, setClients] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [clientsValues, setClientsValues] = useState(initialValues?.clients || []);
  const [establishments, setEstablishments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [establishmentsValues, setEstablishmentsValues] = useState(initialValues?.establishments || []);
  const [areas, setAreas] = useState([]);
  const {space} = useSpace();
  
  useEffect(() => {
    if (initialValues?.roles) {
      setRoles(initialValues?.roles);
    }
    if (initialValues?.clients) {
      setClientsValues(initialValues?.clients);
    }
    if (initialValues?.establishments) {
      setEstablishmentsValues(initialValues?.establishments);
    }
  }, [initialValues?.clients, initialValues?.establishments, initialValues?.roles]);
  
  const structureHidden = useMemo(() => {
    return space?.typeStructure !== 'STRUCTURE';
  }, [space?.typeStructure]);
  
  const establishmentHidden = useMemo(() => {
    return space?.typeStructure === 'AREA';
  }, [space?.typeStructure]);

  const onChangeStatus = useCallback((val) => {
    setStatus(val);
    form.setFieldsValue({verified: Boolean(val === 'ACTIVE')});
  }, [form]);
  
  const hasAuditsRol = useMemo(() => {
    return roles?.some((role) => role?.name === 'Auditoría' || role?.name === 'Audits');
  }, [roles]);
  
  const hasRegisterRol = useMemo(() => {
    return roles?.some((role) => role?.name === 'Registro' || role?.name === 'Register');
  }, [roles]);

  const hasLabDataRol = useMemo(() => {
    return roles?.some((role) => role?.name === 'Laboratorio' || role?.name === 'Laboratory' || role?.name === 'LABORATORY');
  }, [roles]);

  return useMemo(() => {
    const fieldsMap = [...fields];
    if (!isViewMode) {
      fieldsMap.splice(
        4, 0,
        {
          key: 'access',
          colSpan: 2,
          render: createFormFieldset({title: 'user.form.access', translation: 'users'}),
          
        },
      );
      fieldsMap.splice(
        5, 0,
        {
          key: 'roles',
          colSpan: 2,
          label: t('user.form.roles'),
          placeholder: t('form.placeholderSelectRoles'),
          widget: createSearchFieldRemoteRoles({
            usePagination: useSearchRol,
            mode: 'multiple',
            fullEntity: true,
            translation: 'users',
            setRoles,
            userId: id,
          }),
          initialValue: [],
          onlyEdit: true,
          required: true,
        },
      );
      fieldsMap.splice(
        6, 0,
        {
          key: 'clients',
          colSpan: 2,
          label: t('user.form.clients'),
          widget: props => <SelectUserStructures translation={t} setClients={setClients} clients={clients} setClientsValues={setClientsValues} user={id} {...props} />,
          onlyEdit: true,
          extra: !id ? t('user.form.extraClients') : '',
          disabled: id || structureHidden,
          hidden: structureHidden,
        },
      );
      fieldsMap.splice(
        7, 0,
        {
          key: 'establishments',
          colSpan: 2,
          label: t('user.form.establishments'),
          widget: props => <SelectUserEstablishments translation={t} setEstablishments={setEstablishments} establishments={establishments} setEstablishmentsValues={setEstablishmentsValues} user={id} {...props} />,
          initialValue: [],
          onlyEdit: true,
          extra: !id ? t('user.form.extraEstablishments'): '',
          disabled: id || establishmentHidden,
        },
      );
      fieldsMap.splice(
        8, 0,
        {
          key: 'areas',
          colSpan: 2,
          label: t('user.form.areas'),
          widget: props => <SelectUserFacilities translation={t} setAreas={setAreas} areas={areas} user={id} {...props} />,
          onlyEdit: true,
          initialValue: [],
          extra: !id ? t('user.form.extraAreas'): '',
          disabled: id,

        },
      );
      fieldsMap.splice(9, 0,
        {
          key: 'services',
          label: t('user.form.services'),
          onlyEdit: true,
          colSpan: 2,
          dependencies: ['roles'],
          initialValues: initialValues?.services,
          // required: (form?.getFieldValue('roles') === 'select'),
          className:  !hasAuditsRol ? 'hidden' : '',
          // eslint-disable-next-line react/display-name,react/prop-types
          widget: props => <SelectUserServices translation={t} spaceId={space?._id} {...props} />
        });
      fieldsMap?.splice(
        10,0,
        {
          key: 'registersAccess',
          onlyEdit: true,
          colSpan: 2,
          dependencies: ['roles'],
          className:  !hasRegisterRol ? 'hidden' : '',
          widget: ({...props}) => <UserRegisterAccess form={form} initialValues={initialValues?.registersAccess || [{areaAccess: '',category: '', registers: []}]} {...props}/>,
        }
      );
      fieldsMap?.splice(
        11,0,
        {
          key: 'labDataAccess',
          label: t('user.form.labDataAccess'),
          onlyEdit: true,
          colSpan: 2,
          dependencies: ['roles'],
          className:  !hasLabDataRol ? 'hidden' : '',
          widget: ({...props}) => <SelectUserLabDataAccess form={form} initialValues={initialValues?.labDataAccess || []} translation={t} {...props}/>,
        }
      );
      fieldsMap.splice(
        12, 0,
        {
          key: 'accessEnd',
          colSpan: 2,
          render: createFormFieldset({title: '', translation: 'users'}),
        },
      );
      fieldsMap.splice(
        17, 0,
        {
          key: 'status',
          colSpan: 2,
          label: t('user.form.status'),
          onlyEdit: true,
          initialValue: initialValues?.status,
          value: status,
          onChange: onChangeStatus,
          widget: renderStatusSelector,
        },
      );
    }
    return fieldsMap;
  }, [areas, clients, establishmentHidden, establishments, fields, form, hasAuditsRol, hasLabDataRol, hasRegisterRol, id, initialValues?.labDataAccess, initialValues?.registersAccess, initialValues?.services, initialValues?.status, isViewMode, onChangeStatus, space?._id, status, structureHidden, t]);
};

export const useGetUsersByIds = (arrayId) => {
  const filters = new TermFilter({ field: '_id', value: { $in: arrayId } });

  const queryConfig = useMemo(() => {
    return {enabled: arrayId && arrayId.length > 0};
  }, [arrayId]);

  return useQuery([CACHE_KEY_LIST_USERS, filters], async () => {
    const {data} = await UsersApiService.search({
      size: 9999,
      search: '',
      filters
    });
    return data;
  }, queryConfig);
};

export const useGetOne = (_id) => {

  const queryConfig = useMemo(() => {
    return {enabled: Boolean(_id)};
  }, [_id]);

  return useQuery([CACHE_KEY_USERS, _id], async () => {
    const {data} = await UsersApiService.getOne(_id);
    return data;
  }, queryConfig);
};

export const useGetOneAuditorForAudit = (_id, params) => {
  const queryConfig = useMemo(() => {
    return {
      enabled: Boolean(_id),
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    };
  }, [_id]);

  const {isLoading, isError, error, data} = useQuery([CACHE_KEY_USERS, _id, params], async () => {
    if (typeof _id !== 'string') return _id;

    const {data} = await UsersApiService.getOne(_id, params);
    return data;
  }, queryConfig);
  return {isLoading, isError, error, data};
};

export const useGetOneAuditorForAuditFullName = (_id, params) => {
  const queryConfig = useMemo(() => {
    return {
      enabled: Boolean(_id),
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    };
  }, [_id]);

  const {isLoading, isError, error, data} = useQuery([CACHE_KEY_USERS, _id, params], async () => {
    if (typeof _id !== 'string') return _id;

    const {data} = await UsersApiService.getOneFullName(_id, params);
    return data;
  }, queryConfig);
  return {isLoading, isError, error, data};
};
